/**
 * ItensContagemTab - Aba de Itens da Contagem
 * Usando MantineReactTable para filtros, ordenação e paginação
 * Epic 53: Two import buttons for Ordem and Resultado de Contagem
 */
import React, { useMemo } from 'react';
import { Download, Plus } from 'lucide-react';
import {
  MantineReactTable,
  useMantineReactTable,
} from 'mantine-react-table';
import { ValidadeLegend } from './ValidadeLegend';
import { ColumnTooltip, ORDEM_COLUMNS, RESULTADO_COLUMNS } from './ItensContagemColumnTooltip';
import { useItensContagemColumns } from './useItensContagemColumns';
import type { DadoContagem } from '../../hooks/useContagemData';
import { MRT_LOCALIZATION_PT_BR } from '../hospital/tableLocalization';
import PermissionGate from '../PermissionGate';

interface ItensContagemTabProps {
  dadosContagem: DadoContagem[];
  isReadOnly: boolean;
  isEditMode: boolean;
  hideLotsAndValidity: boolean;
  setHideLotsAndValidity: (value: boolean) => void;
  modifiedItems: Map<number, Set<string>>;
  selectedRow: number | null;
  setSelectedRow: (row: number | null) => void;
  downloading: boolean;
  handleDownloadExcel: () => void;
  handleUpdateItemField: (rowIndex: number, field: string, value: number | string) => void;
  openImportModal: () => void;
  openInfoItensModal: () => void;
  openReportModal: () => void;
  closeReportModal: () => void;
  showReportModal: boolean;
  onOpenAddModal?: () => void;
  onDeleteItem?: (itemId: number, codigo: string, lote: string) => void;
  // Epic 53: New import modal handlers
  onOpenImportOrdemModal?: () => void;
  onOpenImportResultadoModal?: () => void;
  // Master user flag (for segregado editing)
  isMaster?: boolean;
  // Global client filter
  globalClienteFilter?: (string | number)[];
  jobClientes?: { id: number; nome_fantasia?: string; razao_social?: string }[];
  // T25: totais agregados no servidor (usados quando nao ha filtro de cliente)
  serverTotals?: { totalSistema: number; totalContado: number } | null;
}

export const ItensContagemTab: React.FC<ItensContagemTabProps> = ({
  dadosContagem,
  isReadOnly,
  isEditMode,
  hideLotsAndValidity,
  setHideLotsAndValidity,
  modifiedItems,
  handleUpdateItemField,
  openImportModal,
  onOpenAddModal,
  onDeleteItem,
  onOpenImportOrdemModal,
  onOpenImportResultadoModal,
  isMaster,
  globalClienteFilter,
  jobClientes,
  serverTotals,
}) => {
  // T25: com filtro de cliente ativo o card deve somar o subconjunto exibido;
  // sem filtro, preferir os agregados do servidor (independem da lista).
  const hasClienteFilter = !!(globalClienteFilter && globalClienteFilter.length > 0);
  const useServerTotals = !hasClienteFilter && serverTotals != null;
  // Consolidation logic for hiding lots and validity
  // Epic 42: Also aggregates faturar and concatenates unique pacientes
  const displayData = useMemo(() => {
    let dataToProcess = dadosContagem;
    if (globalClienteFilter && globalClienteFilter.length > 0 && jobClientes) {
      const selectedNames = new Set<string>();
      globalClienteFilter.forEach(id => {
        const cliente = jobClientes.find(c => c.id === Number(id));
        if (cliente) {
          if (cliente.nome_fantasia) selectedNames.add(cliente.nome_fantasia);
          if (cliente.razao_social) selectedNames.add(cliente.razao_social);
        }
      });
      dataToProcess = dadosContagem.filter(item =>
        item.cliente && selectedNames.has(item.cliente)
      );
    }

    if (!hideLotsAndValidity) return dataToProcess;

    const consolidated = new Map<string, DadoContagem & { _pacientesSet: Set<string> }>();
    dataToProcess.forEach(item => {
      const key = `${item.cliente || '-'}-${item.regime || '-'}-${item.produto}-${item.grupo}`;
      if (!consolidated.has(key)) {
        consolidated.set(key, {
          ...item,
          cliente: item.cliente || '-',
          lote: '-',
          validade: '-',
          faixaAging: '-',
          sistema: 0,
          contado: 0,
          coletado: 0,
          segregado: 0,
          coletar: 0,
          faturar: 0,
          pacientes: '',
          sobras: 0,
          faltas: 0,
          _pacientesSet: new Set<string>(),
        });
      }
      const existing = consolidated.get(key)!;
      existing.sistema += item.sistema;
      existing.contado += item.contado;
      existing.coletado += item.coletado || 0;
      existing.segregado += item.segregado || 0;
      existing.coletar += item.coletar || 0;
      existing.faturar += item.faturar || 0;
      existing.sobras += item.sobras;
      existing.faltas += item.faltas;
      if (item.pacientes) {
        item.pacientes.split(',').forEach(p => {
          const trimmed = p.trim();
          if (trimmed) existing._pacientesSet.add(trimmed);
        });
      }
    });

    return Array.from(consolidated.values()).map(item => {
      const { _pacientesSet, ...rest } = item;
      return { ...rest, pacientes: Array.from(_pacientesSet).join(', ') };
    });
  }, [dadosContagem, hideLotsAndValidity, globalClienteFilter, jobClientes]);

  const { columns, rowActions } = useItensContagemColumns({
    hideLotsAndValidity,
    isReadOnly,
    isEditMode,
    isMaster: isMaster ?? false,
    modifiedItems,
    handleUpdateItemField,
    onDeleteItem,
    displayData,
  });

  const table = useMantineReactTable({
    columns,
    data: displayData,
    enableColumnFilters: true,
    enableSorting: true,
    enablePagination: true,
    enableColumnResizing: true,
    enableRowActions: isEditMode,
    positionActionsColumn: 'last',
    enableDensityToggle: false,
    enableStickyHeader: true,
    mantineTableContainerProps: { sx: { maxHeight: '60vh' } },
    initialState: {
      showColumnFilters: true,
      density: 'xs',
      pagination: { pageIndex: 0, pageSize: 50 },
      sorting: [{ id: 'cliente', desc: false }],
    },
    mantineTableProps: {
      highlightOnHover: true,
      striped: true,
      withTableBorder: true,
      withColumnBorders: true,
      sx: {
        'th': { padding: '8px 12px !important', fontSize: '0.675rem' },
        'td': { padding: '4px 12px !important', fontSize: '0.675rem' }
      }
    },
    renderRowActions: rowActions as any,
    mantinePaperProps: { shadow: 'none', withBorder: true, radius: 'md' },
    localization: MRT_LOCALIZATION_PT_BR,
  });

  return (
    <div className="p-6 space-y-6">
      {/* Epic 53: Toolbar com 2 botões de importação */}
      <div className="flex items-center justify-between pb-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-2">
          <PermissionGate tela="CONTAGENS_LIST" acao="criar">
            <button
              onClick={onOpenImportOrdemModal || openImportModal}
              className="flex items-center space-x-2 px-4 py-2.5 bg-gradient-to-r from-emerald-500 to-green-500 text-white rounded-lg hover:from-emerald-600 hover:to-green-600 transition shadow-sm font-medium"
            >
              <Download size={18} className="rotate-180" />
              <span>Importar Base da Contagem</span>
            </button>
          </PermissionGate>
          <ColumnTooltip columns={ORDEM_COLUMNS} color="emerald" />

          <div className="flex items-center ml-4 space-x-2 bg-gray-100 dark:bg-gray-700 px-3 py-2 rounded-lg">
            <div
              className={`relative w-10 h-5 rounded-full cursor-pointer transition-colors duration-300 ${hideLotsAndValidity ? 'bg-blue-600' : 'bg-gray-400'}`}
              onClick={() => setHideLotsAndValidity(!hideLotsAndValidity)}
            >
              <div className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow-md transform transition-transform duration-300 ${hideLotsAndValidity ? 'translate-x-5' : 'translate-x-0'}`} />
            </div>
            <span
              className="text-sm font-medium text-gray-700 dark:text-gray-300 select-none cursor-pointer"
              onClick={() => setHideLotsAndValidity(!hideLotsAndValidity)}
            >
              Ocultar lotes e validade
            </span>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <PermissionGate tela="CONTAGENS_LIST" acao="criar">
            <button
              onClick={onOpenImportResultadoModal || openImportModal}
              className="flex items-center space-x-2 px-4 py-2.5 bg-gradient-to-r from-blue-500 to-indigo-500 text-white rounded-lg hover:from-blue-600 hover:to-indigo-600 transition shadow-sm font-medium"
            >
              <Download size={18} className="rotate-180" />
              <span>Importar Resultado Contagem</span>
            </button>
          </PermissionGate>
          <ColumnTooltip columns={RESULTADO_COLUMNS} color="blue" position="left" />
        </div>
      </div>

      {/* Header com contagem de itens e botão adicionar */}
      <div className="flex items-center justify-end flex-wrap gap-4">
        <div className="flex items-center space-x-3">
          {isEditMode && onOpenAddModal && (
            <PermissionGate tela="CONTAGENS_LIST" acao="criar">
              <button
                onClick={onOpenAddModal}
                className="flex items-center space-x-2 px-4 py-2.5 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-lg hover:from-green-600 hover:to-emerald-600 transition shadow-sm font-medium"
              >
                <Plus size={18} />
                <span>Adicionar Item</span>
              </button>
            </PermissionGate>
          )}
          <div className="text-sm text-gray-500">
            <span className="font-semibold">{displayData.length}</span>{' '}
            {hideLotsAndValidity ? 'produtos consolidados' : 'itens na contagem'}
            {globalClienteFilter && globalClienteFilter.length > 0 && dadosContagem.length !== displayData.length && (
              <span className="ml-1 text-blue-500">(de {dadosContagem.length} total)</span>
            )}
          </div>
        </div>
      </div>

      {/* Epic 49: Legenda de cores de vencimento */}
      {!hideLotsAndValidity && displayData.length > 0 && (
        <ValidadeLegend className="mb-2" />
      )}

      {/* Tabela com MantineReactTable */}
      <MantineReactTable table={table} />

      {/* Summary Card */}
      {displayData.length > 0 && (
        <div className="bg-emerald-50 dark:bg-emerald-900/20 border-2 border-emerald-200 dark:border-emerald-700 rounded-lg p-4">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
            <div>
              <span className="text-gray-600 dark:text-gray-400">Total Itens:</span>
              <span className="ml-2 font-bold text-emerald-700 dark:text-emerald-400">{displayData.length}</span>
            </div>
            <div>
              <span className="text-gray-600 dark:text-gray-400">Total Sistema:</span>
              <span className="ml-2 font-bold text-emerald-700 dark:text-emerald-400">
                {useServerTotals
                  ? serverTotals!.totalSistema
                  : displayData.reduce((sum, item) => sum + item.sistema, 0)}
              </span>
            </div>
            <div>
              <span className="text-gray-600 dark:text-gray-400">Total Contado:</span>
              <span className="ml-2 font-bold text-emerald-700 dark:text-emerald-400">
                {useServerTotals
                  ? serverTotals!.totalContado
                  : displayData.reduce((sum, item) => sum + item.contado, 0)}
              </span>
            </div>
            <div>
              <span className="text-gray-600 dark:text-gray-400">Clientes:</span>
              <span className="ml-2 font-bold text-emerald-700 dark:text-emerald-400">
                {new Set(displayData.map(d => d.cliente).filter(Boolean)).size}
              </span>
            </div>
            <div>
              <span className="text-gray-600 dark:text-gray-400">Produtos únicos:</span>
              <span className="ml-2 font-bold text-emerald-700 dark:text-emerald-400">
                {new Set(displayData.map(d => d.produto)).size}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
